import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { useAppContext } from '../../contexts/AppContext';
import { apiService, WhatsAppCustomTemplate } from '../../services/api';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

// Same status/quality → color mapping as TemplateManagementScreen.tsx and
// TemplateMessagesCard.tsx, reused verbatim for visual consistency.
const STATUS_META: Record<string, { color: string; label: string }> = {
  APPROVED: { color: '#10B981', label: 'Approved' },
  PENDING: { color: '#F59E0B', label: 'Pending review' },
  REJECTED: { color: '#EF4444', label: 'Rejected' },
  PAUSED: { color: '#F59E0B', label: 'Paused' },
  DISABLED: { color: '#EF4444', label: 'Disabled' },
};

const QUALITY_META: Record<string, { color: string; label: string }> = {
  GREEN: { color: '#10B981', label: 'High quality' },
  YELLOW: { color: '#F59E0B', label: 'Medium quality' },
  RED: { color: '#EF4444', label: 'Low quality' },
  UNKNOWN: { color: colors.textLight as string, label: 'Quality pending' },
};

/** Full template list reachable from the dashboard's Template Messages card
 * "View all" CTA. Load-all (business-scale, not conversation-scale, same
 * order of magnitude as Team). Row tap navigates to the existing
 * template-management screen (list mode) — that screen owns edit mode
 * internally and doesn't accept an initial-template prop, so this stays a
 * plain navigation rather than a deep link into edit mode. */
export const DashboardTemplatesAllScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();

  const [templates, setTemplates] = useState<WhatsAppCustomTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await apiService.getWhatsAppCustomTemplates();
        const data = Array.isArray(res.data) ? res.data : res.data?.templates;
        setTemplates(data ?? []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      t.templateName?.toLowerCase().includes(q) ||
      t.category?.toLowerCase().includes(q) ||
      t.language?.toLowerCase().includes(q)
    );
  }, [templates, query]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('dashboard')} activeOpacity={0.7}>
          <DirectionalIcon name="chevron-back" size={20} color={colors.text.primary as string} />
        </TouchableOpacity>
        <View>
          <Text style={styles.headerTitle}>Template messages</Text>
          <Text style={styles.headerSubtitle}>{templates.length} template{templates.length === 1 ? '' : 's'}</Text>
        </View>
        <TouchableOpacity style={styles.settingsButton} onPress={() => navigateTo('template-management')} activeOpacity={0.7}>
          <Ionicons name="settings-outline" size={18} color={colors.text.primary as string} />
        </TouchableOpacity>
      </View>

      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={16} color={colors.text.secondary as string} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search name, category, language"
          placeholderTextColor={colors.text.secondary as string}
          value={query}
          onChangeText={setQuery}
        />
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary as string} />
        </View>
      ) : (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {filtered.length === 0 ? (
            <View style={styles.empty}>
              <Ionicons name="document-text-outline" size={24} color={colors.text.secondary as string} />
              <Text style={styles.emptyText}>{templates.length === 0 ? 'No templates yet' : 'No matches'}</Text>
            </View>
          ) : (
            filtered.map((t) => {
              const statusMeta = t.metaStatus ? STATUS_META[t.metaStatus] : undefined;
              const qualityMeta = t.metaQualityScore ? QUALITY_META[t.metaQualityScore] : undefined;
              return (
                <TouchableOpacity
                  key={t._id}
                  style={styles.row}
                  onPress={() => navigateTo('template-management')}
                  activeOpacity={0.7}
                >
                  <View style={styles.rowContent}>
                    <Text style={styles.name} numberOfLines={1}>{t.templateName}</Text>
                    <Text style={styles.meta} numberOfLines={1}>{t.category} · {t.language}</Text>
                  </View>
                  <View style={styles.badges}>
                    {statusMeta && (
                      <View style={[styles.badge, { backgroundColor: `${statusMeta.color}20` }]}>
                        <Text style={[styles.badgeText, { color: statusMeta.color }]}>{statusMeta.label}</Text>
                      </View>
                    )}
                    {qualityMeta && (
                      <View style={styles.statusLine}>
                        <View style={[styles.statusDot, { backgroundColor: qualityMeta.color }]} />
                        <Text style={styles.statusText}>{qualityMeta.label}</Text>
                      </View>
                    )}
                  </View>
                  <DirectionalIcon name="chevron-forward" size={16} color={colors.text.secondary as string} />
                </TouchableOpacity>
              );
            })
          )}
        </ScrollView>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.base,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  backButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  settingsButton: {
    marginLeft: 'auto',
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    margin: designTokens.spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: designTokens.spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: 38,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: designTokens.spacing.md,
    paddingBottom: designTokens.spacing.lg,
    gap: designTokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.sm,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  meta: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  badges: {
    alignItems: 'flex-end',
    gap: 4,
  },
  badge: {
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
  },
  statusLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
});
